// Pricing / plan-selection model for the /products storefront.
//
// Three levels of purchase:
//   1. A single APP — every app (incl. each sub-app of a group) has its own price.
//   2. A GROUP — Woods, Beams and Loads bundle their sub-apps at a group price
//      (cheaper than buying the sub-apps separately). A FORMING group
//      (`groupPriceNok: null`) shows its members but has no group price yet —
//      drawn dashed, like planned construction on a structural drawing.
//   3. ALL APPS — the whole suite; or a DEDICATED private server.
// Price follows COMPUTE WEIGHT (light / advanced / heavy) — see ResourceClass.
//
// CONFIRMED today: Woods group 1900 (Tverrsnitt 499, Forbindelser 899,
// Veggskiver 899, Forankring 899), Beams group 1900 (899 per sub-app), Loads
// group 1900 (Wind 1499, Snow 899), ConcreteBeam 2500, ConcreteShell 2500,
// FEM 4900, Foundations 1500 (beta price), ConcreteResponse 2900, All Apps
// 9500 (includes ConcreteResponse and Spikerplater). Spikerplater 899 is
// indicative (as Woods Forbindelser, the nearest product) until the product
// owner sets it. A price marked `priceStatus` carries a
// badge and a footnote: 'indicative' until the product owner sets it, 'beta'
// while the app is in beta.

export type ResourceClass = 'light' | 'advanced' | 'heavy'
export type Interval = 'monthly' | 'annual'
export type PlanMode = 'mix' | 'all' | 'dedicated'
export type PriceStatus = 'indicative' | 'beta'

/** How a live app is reached: opened in the browser, or downloaded as a Windows installer from /download/<id>. */
export type AppLaunch = { kind: 'web'; url: string } | { kind: 'download' }

/** A leaf app — either standalone, or a sub-app inside a group. Individually priced. */
export interface PricingApp {
  id: string
  /** Resolves `${i18nKey}.name`, `.tagline`, `.pitch`. */
  i18nKey: string
  /** Absent while the app is not live yet. */
  launch?: AppLaunch
  icon: string
  resourceClass: ResourceClass
  priceNok: number
  priceStatus?: PriceStatus
  /** Computational method shown as a mono title-block tag (e.g. "3D-MCFT"). */
  methodTag?: string
}

/** A collection of sub-apps sold either à la carte or as one group price.
 *  `groupPriceNok: null` = the group is still forming: members are sold
 *  individually and the group itself cannot be selected yet. */
export interface PricingGroup {
  id: string
  i18nKey: string
  icon: string
  groupPriceNok: number | null
  priceStatus?: PriceStatus
  members: PricingApp[]
}

export const pricingGroups: PricingGroup[] = [
  {
    id: 'woods', i18nKey: 'cat.woods', icon: 'stack', groupPriceNok: 1900,
    members: [
      { id: 'woods-tverrsnitt', i18nKey: 'cat.tverrsnitt', launch: { kind: 'web', url: 'https://woods.structuramatrix.no/tverrsnitt' }, icon: 'bounding-box', resourceClass: 'light', priceNok: 499 },
      { id: 'woods-forbindelser', i18nKey: 'cat.forbindelser', launch: { kind: 'web', url: 'https://woods.structuramatrix.no/forbindelser' }, icon: 'nut', resourceClass: 'advanced', priceNok: 899 },
      { id: 'woods-skiver', i18nKey: 'cat.skiver', launch: { kind: 'web', url: 'https://woods.structuramatrix.no/skive' }, icon: 'bricks', resourceClass: 'advanced', priceNok: 899 },
      { id: 'woods-forankring', i18nKey: 'cat.forankring', launch: { kind: 'web', url: 'https://woods.structuramatrix.no/forankring' }, icon: 'arrow-up-circle', resourceClass: 'advanced', priceNok: 899 },
    ],
  },
  {
    id: 'beams', i18nKey: 'cat.beams', icon: 'diagram-3', groupPriceNok: 1900,
    members: [
      { id: 'beams-betong', i18nKey: 'cat.beams-betong', launch: { kind: 'web', url: 'https://concrete.structuramatrix.no' }, icon: 'bricks', resourceClass: 'advanced', priceNok: 899 },
      { id: 'beams-stal', i18nKey: 'cat.beams-stal', launch: { kind: 'web', url: 'https://steel.structuramatrix.no' }, icon: 'hexagon', resourceClass: 'advanced', priceNok: 899 },
      { id: 'beams-tre', i18nKey: 'cat.beams-tre', launch: { kind: 'web', url: 'https://wood.structuramatrix.no' }, icon: 'view-stacked', resourceClass: 'advanced', priceNok: 899 },
    ],
  },
  {
    id: 'loads', i18nKey: 'cat.loads', icon: 'arrow-down-circle', groupPriceNok: 1900,
    members: [
      { id: 'wind', i18nKey: 'cat.wind', launch: { kind: 'web', url: 'https://wind.structuramatrix.no' }, icon: 'wind', resourceClass: 'heavy', priceNok: 1499 },
      { id: 'snow', i18nKey: 'cat.snow', launch: { kind: 'web', url: 'https://snow.structuramatrix.no' }, icon: 'cloud-snow', resourceClass: 'advanced', priceNok: 899 },
    ],
  },
]

/** The heavy end of the suite — response and analysis tools sold one by one. */
export const analysisApps: PricingApp[] = [
  { id: 'concretebeam', i18nKey: 'cat.concretebeam', launch: { kind: 'web', url: 'https://concretebeam.structuramatrix.no' }, icon: 'activity', resourceClass: 'advanced', priceNok: 2500, methodTag: '2D-MCFT' },
  { id: 'concreteshell', i18nKey: 'cat.concreteshell', launch: { kind: 'web', url: 'https://concreteshell.structuramatrix.no' }, icon: 'hexagon-half', resourceClass: 'advanced', priceNok: 2500, methodTag: '3D-MCFT' },
  { id: 'fem', i18nKey: 'cat.fem', launch: { kind: 'web', url: 'https://fem.structuramatrix.no' }, icon: 'grid-3x3-gap', resourceClass: 'heavy', priceNok: 4900, methodTag: 'NL-FEM' },
  { id: 'foundations', i18nKey: 'cat.foundations', launch: { kind: 'web', url: 'https://foundations.structuramatrix.no' }, icon: 'layers', resourceClass: 'heavy', priceNok: 1500, priceStatus: 'beta', methodTag: 'FEM+EC7' },
  { id: 'concreteresponse', i18nKey: 'cat.concreteresponse', launch: { kind: 'download' }, icon: 'pc-display', resourceClass: 'advanced', priceNok: 2900, methodTag: 'MCFT/DSFM' },
  { id: 'spikerplater', i18nKey: 'cat.spikerplater', launch: { kind: 'download' }, icon: 'grid-3x2-gap', resourceClass: 'advanced', priceNok: 899, priceStatus: 'indicative', methodTag: 'EC5 §8.8' },
]

const pricedItems: readonly { priceStatus?: PriceStatus }[] = [...pricingGroups, ...pricingGroups.flatMap((g) => g.members), ...analysisApps]

function anyPriceMarked(status: PriceStatus): boolean {
  return pricedItems.some((item) => item.priceStatus === status)
}

/** Each drives its own storefront footnote. */
export const HAS_INDICATIVE_PRICES: boolean = anyPriceMarked('indicative')
export const HAS_BETA_PRICES: boolean = anyPriceMarked('beta')

export const RESOURCE_WEIGHT: Record<ResourceClass, number> = { light: 1, advanced: 2, heavy: 3 }

export const ALL_APPS_PRICE_NOK: number = 9500
export const ANNUAL_MONTHS_CHARGED = 10
export const MONTHS_PER_YEAR = 12

const DISCOUNT_TIERS: ReadonlyArray<{ min: number; pct: number }> = [
  { min: 4, pct: 0.20 },
  { min: 3, pct: 0.15 },
  { min: 2, pct: 0.10 },
]

export function volumeDiscountPct(itemCount: number): number {
  for (const tier of DISCOUNT_TIERS) if (itemCount >= tier.min) return tier.pct
  return 0
}

export function monthlyEquivalent(monthly: number, interval: Interval): number {
  return interval === 'annual' ? (monthly * ANNUAL_MONTHS_CHARGED) / MONTHS_PER_YEAR : monthly
}

/** A resolved line on the plan: a whole group, a sub-app, or a standalone app. */
export interface LineItem {
  id: string
  nameKey: string
  priceNok: number
  isGroup: boolean
}

/** Turn a set of selected ids into billable lines. A selected group replaces its
 *  members with the single group price; otherwise each selected member counts. */
export function collectLineItems(selected: ReadonlySet<string>): LineItem[] {
  const items: LineItem[] = []
  for (const g of pricingGroups) {
    if (g.groupPriceNok !== null && selected.has(g.id)) {
      items.push({ id: g.id, nameKey: `${g.i18nKey}.name`, priceNok: g.groupPriceNok, isGroup: true })
    } else {
      for (const m of g.members) if (selected.has(m.id)) items.push({ id: m.id, nameKey: `${m.i18nKey}.name`, priceNok: m.priceNok, isGroup: false })
    }
  }
  for (const a of analysisApps) if (selected.has(a.id)) items.push({ id: a.id, nameKey: `${a.i18nKey}.name`, priceNok: a.priceNok, isGroup: false })
  return items
}

export interface MixTotals {
  items: LineItem[]
  count: number
  subtotalMonthly: number
  discountPct: number
  discountedMonthly: number
  allAppsCheaper: boolean
}

export function computeMix(selected: ReadonlySet<string>): MixTotals {
  const items = collectLineItems(selected)
  const subtotalMonthly = items.reduce((s, i) => s + i.priceNok, 0)
  const discountPct = volumeDiscountPct(items.length)
  const discountedMonthly = Math.round(subtotalMonthly * (1 - discountPct))
  return {
    items,
    count: items.length,
    subtotalMonthly,
    discountPct,
    discountedMonthly,
    allAppsCheaper: items.length >= 2 && discountedMonthly > ALL_APPS_PRICE_NOK,
  }
}

/** When ≥2 members of a group are selected individually and their sum beats the
 *  group price, suggest switching to the whole group. */
export interface GroupUpsell {
  groupId: string
  nameKey: string
  groupPriceNok: number
  memberSum: number
}

export function groupUpsells(selected: ReadonlySet<string>): GroupUpsell[] {
  const out: GroupUpsell[] = []
  for (const g of pricingGroups) {
    if (g.groupPriceNok === null || selected.has(g.id)) continue
    const chosen = g.members.filter((m) => selected.has(m.id))
    const memberSum = chosen.reduce((s, m) => s + m.priceNok, 0)
    if (chosen.length >= 2 && memberSum > g.groupPriceNok) {
      out.push({ groupId: g.id, nameKey: `${g.i18nKey}.name`, groupPriceNok: g.groupPriceNok, memberSum })
    }
  }
  return out
}

export function formatNok(amount: number): string {
  return amount.toLocaleString('nb-NO')
}
